import React, { useState } from "react";
import "../../Styles/CmrFundsForm.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { cmrfAPI } from "../../api/index";

const MANDAL_NAMES = [
  "Kandukur",
  "Lingasamudram",
  "Gudluru",
  "Ulavapadu",
  "Voletivaripalem",
];

// TEMPORARY — village name suggestions per mandal, for the autocomplete
// datalist below. This is NOT validated client-side; the backend is still
// the source of truth. Replace/extend this once you have the real seeded
// village list per mandal (e.g. from db/schema.sql seed data or a future
// GET /villages endpoint) so users get accurate suggestions instead of
// typing blind.
const VILLAGE_SUGGESTIONS = {
  Kandukur: ["Kandukur", "Kalavalla"],
  Lingasamudram: [],
  Gudluru: ["Gudluru"],
  Ulavapadu: [],
  Voletivaripalem: [],
};

const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  disbursed: "Disbursed",
};

const INITIAL_FORM = {
  name: "",
  relation: "",
  voterId: "",
  amount: "",
  village: "",
  mandal: "",
  date: "",
  status: "",
  remarks: "",
  videoUrl: "",
};

// Order used to focus the first invalid field on submit (same as the form layout)
const FIELD_ORDER = [
  "name",
  "relation",
  "voterId",
  "amount",
  "mandal",
  "village",
  "date",
  "status",
  "videoUrl",
  "remarks",
];

// These fields show a format error immediately while typing
const LIVE_VALIDATE_FIELDS = ["voterId", "amount", "village"];

const NAME_REGEX = /^[A-Za-z\s.']+$/;
const RELATION_REGEX = /^[A-Za-z\s./'\-]+$/;
const VILLAGE_REGEX = /^[A-Za-z\s]+$/;
const VOTER_ID_REGEX = /^[A-Z]{3}[0-9]{7}$/;
const AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;

const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}).`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

const isLikelyVideoUrl = (url) => {
  if (!url.trim()) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
};

// ---------- Field validation rules ----------
const validateField = (name, value) => {
  const v = typeof value === "string" ? value.trim() : value;

  switch (name) {
    case "name":
      if (!v) return "Beneficiary name is required";
      if (v.length < 3) return "Name must be at least 3 characters";
      if (v.length > 100) return "Name must not exceed 100 characters";
      if (!NAME_REGEX.test(v)) return "Name can contain only alphabets, spaces, '.' and '''";
      return "";

    case "relation":
      if (!v) return "Relation is required (e.g. S/O Ramesh)";
      if (v.length < 2) return "Relation must be at least 2 characters";
      if (v.length > 100) return "Relation must not exceed 100 characters";
      if (!RELATION_REGEX.test(v))
        return "Relation can contain only alphabets, spaces and . / ' -";
      return "";

    case "voterId":
      if (!v) return "Voter Id is required";
      if (!VOTER_ID_REGEX.test(v))
        return "Enter a valid Voter Id (3 letters + 7 digits, e.g. ABC1234567)";
      return "";

    case "amount":
      if (!v) return "Amount is required";
      if (!AMOUNT_REGEX.test(v)) return "Enter numbers only (e.g. 50000 or 50000.50)";
      if (Number(v) <= 0) return "Amount must be greater than 0";
      if (Number(v) > 1000000000) return "Amount is too large";
      return "";

    case "mandal":
      if (!v) return "Please select a Mandal";
      if (!MANDAL_NAMES.includes(v)) return "Please select a valid Mandal";
      return "";

    case "village":
      if (!v) return "Village is required";
      if (!VILLAGE_REGEX.test(v)) return "Village name can contain only alphabets (A-Z)";
      if (v.length < 2) return "Village name must be at least 2 characters";
      if (v.length > 100) return "Village name must not exceed 100 characters";
      return "";

    case "date": {
      if (!v) return "Date is required";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "Enter a valid date";
      if (d.getFullYear() < 1990) return "Date cannot be before 1990";
      if (d.getFullYear() > 2100) return "Date cannot be after 2100";
      return "";
    }

    case "status":
      if (!v) return "Please select a Status";
      if (!STATUS_OPTIONS.includes(v)) return "Please select a valid Status";
      return "";

    case "videoUrl":
      if (!v) return ""; // optional
      if (!isLikelyVideoUrl(v))
        return "Enter a valid link starting with http:// or https:// (e.g. a YouTube link)";
      return "";

    case "remarks":
      if (!v) return ""; // optional
      if (v.length < 5) return "Remarks must be at least 5 characters";
      if (v.length > 500) return "Remarks must not exceed 500 characters";
      return "";

    default:
      return "";
  }
};

const validateAll = (data) => {
  const errors = {};
  FIELD_ORDER.forEach((name) => {
    const msg = validateField(name, data[name]);
    if (msg) errors[name] = msg;
  });
  return errors;
};

const CmrFundsForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const showError = (name) => touched[name] && errors[name];
  const inputClass = (name) => (showError(name) ? "input-error" : "");

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Voter Id: force uppercase and remove spaces
    if (name === "voterId") value = value.toUpperCase().replace(/\s/g, "");

    setFormData((prev) => ({ ...prev, [name]: value }));

    const isLive = LIVE_VALIDATE_FIELDS.includes(name);
    if (touched[name] || isLive) {
      if (isLive && !touched[name]) {
        setTouched((prev) => ({ ...prev, [name]: true }));
      }
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setTouched({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate every field and mark all as touched so every error is shown
    const newErrors = validateAll(formData);
    setErrors(newErrors);
    setTouched(FIELD_ORDER.reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    const firstInvalid = FIELD_ORDER.find((f) => newErrors[f]);
    if (firstInvalid) {
      toast.error("Please fix the highlighted fields");
      const el = document.querySelector(`[name="${firstInvalid}"]`);
      if (el) el.focus();
      return;
    }

    const trimmedVillage = formData.village.trim();

    const payload = {
      beneficiary_name: formData.name.trim(),
      relation_name: formData.relation.trim() || undefined,
      epic_no: formData.voterId.trim() || undefined,
      amount: formData.amount ? Number(formData.amount) : undefined,
      mandal_name: formData.mandal,
      village_name: trimmedVillage,
      application_date: formData.date || undefined,
      status: formData.status,
      video_url: formData.videoUrl.trim() || undefined,
      remarks: formData.remarks.trim() || undefined,
    };

    setSubmitting(true);
    try {
      await cmrfAPI.create(payload);
      toast.success("CMRF beneficiary added successfully");
      handleReset();
      navigate("/dashboard/cmrf-table");
    } catch (err) {
      console.error("CMRF create payload sent:", payload);
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const villageOptions = VILLAGE_SUGGESTIONS[formData.mandal] || [];

  return (
    <div className="beneficiary-form-container">
      <div className="form-card">
        <div className="form-header">
          <h2>Add New CMRF Beneficiary</h2>
          <button type="button" className="back-btn" onClick={() => window.history.back()}>
            Back
          </button>
        </div>

        {/* noValidate -> disables browser default popups so our custom messages show */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-grid">
            <div className="form-group">
              <label>Beneficiary Name *</label>
              <input
                type="text"
                name="name"
                placeholder="Enter Beneficiary Name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("name")}
                maxLength={100}
              />
              {showError("name") && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label>Relation (Ex: S/O, W/O, D/O)*</label>
              <input
                type="text"
                name="relation"
                placeholder="Ex: S/O, W/O, D/O"
                value={formData.relation}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("relation")}
                maxLength={100}
              />
              {showError("relation") && <span className="error-text">{errors.relation}</span>}
            </div>

            <div className="form-group">
              <label>Voter Id *</label>
              <input
                type="text"
                name="voterId"
                placeholder="Enter VoterId (e.g. ABC1234567)"
                value={formData.voterId}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("voterId")}
                maxLength={10}
              />
              {showError("voterId") && <span className="error-text">{errors.voterId}</span>}
            </div>

            <div className="form-group">
              <label>Amount (₹) *</label>
              {/* type="text" + inputMode="decimal" so we can show our own error for non-numeric input */}
              <input
                type="text"
                inputMode="decimal"
                name="amount"
                placeholder="Enter Amount (numbers only)"
                value={formData.amount}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("amount")}
                maxLength={13}
              />
              {showError("amount") && <span className="error-text">{errors.amount}</span>}
            </div>

            <div className="form-group">
              <label>Mandal *</label>
              <select
                name="mandal"
                value={formData.mandal}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mandal")}
              >
                <option value="">Select Mandal</option>
                {MANDAL_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {showError("mandal") && <span className="error-text">{errors.mandal}</span>}
            </div>

            <div className="form-group">
              <label>Village *</label>
              <input
                type="text"
                name="village"
                placeholder="Enter Village (alphabets only)"
                value={formData.village}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("village")}
                list="village-suggestions"
                autoComplete="off"
                maxLength={100}
              />
              <datalist id="village-suggestions">
                {villageOptions.map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
              {showError("village") && <span className="error-text">{errors.village}</span>}
              <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Must exactly match an existing village name under the selected Mandal.
                Select a Mandal first to see suggestions — double-check spelling either way.
              </small>
            </div>

            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("date")}
                min="1990-01-01"
                max="2100-12-31"
              />
              {showError("date") && <span className="error-text">{errors.date}</span>}
            </div>

            <div className="form-group">
              <label>Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("status")}
              >
                <option value="">Select Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
              {showError("status") && <span className="error-text">{errors.status}</span>}
            </div>

            <div className="form-group">
              <label>Video URL (YouTube or direct link) (Optional)</label>
              <input
                type="url"
                name="videoUrl"
                placeholder="https://www.youtube.com/watch?v=..."
                value={formData.videoUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("videoUrl")}
              />
              {showError("videoUrl") && <span className="error-text">{errors.videoUrl}</span>}
            </div>
          </div>

          <div className="form-group full-width">
            <label>Remarks (Optional)</label>
            <textarea
              rows="4"
              name="remarks"
              placeholder="Enter Remarks"
              value={formData.remarks}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("remarks")}
              maxLength={500}
            ></textarea>
            {showError("remarks") && <span className="error-text">{errors.remarks}</span>}
            <small className="char-count">{formData.remarks.length}/500</small>
          </div>

          <div className="btn-group">
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button type="button" className="reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CmrFundsForm;